<template>
  <div class="ob-col ob-col--wide">
    <div class="ob-head">
      <span class="ob-head__icon" style="background: rgba(59, 130, 246, 0.1); color: #3b82f6"><span class="mdi mdi-domain"></span></span>
      <h2 class="ob-head__title">Cuéntanos sobre tu parque</h2>
      <p class="ob-head__sub">Esta información aparecerá en tus documentos y estados de cuenta</p>
    </div>

    <div class="park">
      <div class="park__form">
        <div class="ob-field">
          <label class="ob-label" for="ob-name">Nombre del parque</label>
          <input id="ob-name" v-model="ob.park.name" class="ob-input" placeholder="Ej: Parque Industrial Bajío Norte" maxlength="80" autocomplete="off" />
        </div>
        <div class="ob-field">
          <label class="ob-label" for="ob-rfc">RFC</label>
          <div class="ob-input-wrap">
            <input id="ob-rfc" v-model="rfc" class="ob-input ob-input--mono" :class="{ 'ob-input--ok': rfcOk }" placeholder="ABC123456XYZ" maxlength="13" autocomplete="off" />
            <span v-if="rfcOk" class="ob-check mdi mdi-check-circle"></span>
          </div>
        </div>
        <div class="ob-field">
          <label class="ob-label" for="ob-mail">Correo de contacto del parque</label>
          <div class="ob-input-wrap">
            <input id="ob-mail" v-model="ob.park.contactEmail" type="email" class="ob-input" :class="{ 'ob-input--ok': emailOk }" placeholder="contacto@tuparque.com" autocomplete="off" />
            <span v-if="emailOk" class="ob-check mdi mdi-check-circle"></span>
          </div>
        </div>

        <hr class="ob-divider" />
        <div class="ob-section-label">Ubicación del parque</div>
        <div class="ob-field">
          <label class="ob-label" for="ob-address">Dirección</label>
          <input id="ob-address" v-model="ob.park.address" class="ob-input" placeholder="Calle, número y colonia o carretera y kilómetro" maxlength="250" autocomplete="off" />
        </div>
        <div class="ob-row2">
          <div class="ob-field">
            <label class="ob-label" for="ob-city">Ciudad</label>
            <input id="ob-city" v-model="ob.park.city" class="ob-input" placeholder="Ej: Querétaro" maxlength="100" autocomplete="off" />
          </div>
          <div class="ob-field">
            <label class="ob-label" for="ob-state">Estado</label>
            <input id="ob-state" v-model="ob.park.state" class="ob-input" placeholder="Ej: Querétaro" maxlength="100" autocomplete="off" />
          </div>
        </div>
        <p class="ob-note">Con la ciudad y el estado mostramos el clima de tu parque en el panel.</p>

        <hr class="ob-divider" />
        <div class="ob-field">
          <span class="ob-label">Logo</span>
          <div
            class="drop"
            :class="{ 'drop--over': dragOver, 'drop--has': !!ob.park.logoPreview }"
            @dragover.prevent="dragOver = true"
            @dragleave="dragOver = false"
            @drop.prevent="onDrop"
            @click="fileInput.click()"
          >
            <img v-if="ob.park.logoPreview" :src="ob.park.logoPreview" alt="Logo del parque" class="drop__img" />
            <template v-else>
              <span class="mdi mdi-cloud-upload-outline drop__icon"></span>
              <span class="drop__text">Arrastra tu logo o haz clic para elegirlo</span>
            </template>
            <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/svg+xml" hidden @change="onFile" />
          </div>
          <p class="ob-note">Puedes agregarlo después desde Configuración</p>
          <p v-if="logoError" class="ob-note" style="color: #b91c1c">{{ logoError }}</p>
        </div>

        <hr class="ob-divider" />
        <div class="ob-section-label">Datos bancarios para pagos</div>
        <div class="ob-field">
          <label class="ob-label" for="ob-bank">Banco</label>
          <input id="ob-bank" v-model="ob.park.bankName" class="ob-input" placeholder="Ej: BBVA, Santander, Banorte" maxlength="60" autocomplete="off" />
        </div>
        <div class="ob-field">
          <label class="ob-label" for="ob-ben">Beneficiario</label>
          <input id="ob-ben" v-model="ob.park.bankBeneficiary" class="ob-input" placeholder="Nombre tal como aparece en la cuenta" maxlength="100" autocomplete="off" />
        </div>
        <div class="ob-field">
          <label class="ob-label" for="ob-clabe">CLABE interbancaria</label>
          <div class="ob-input-wrap">
            <input id="ob-clabe" :value="clabeShown" class="ob-input ob-input--mono" :class="{ 'ob-input--ok': clabeOk }" placeholder="000000 000000 000000" inputmode="numeric" autocomplete="off" @input="onClabe" />
            <span v-if="clabeOk" class="ob-check mdi mdi-check-circle"></span>
          </div>
          <p v-if="clabeCount && !clabeOk" class="ob-note">{{ clabeCount }} de 18 dígitos</p>
        </div>
        <p class="ob-note">Esta información aparecerá en tus estados de cuenta para que los inquilinos sepan a dónde transferir.</p>
        <p class="ob-note ob-note--soft">Puedes agregarlo después desde Configuración.</p>

        <p v-if="ob.error" class="ob-error">{{ ob.error }}</p>
      </div>

      <aside class="park__preview" aria-label="Vista previa del estado de cuenta">
        <div class="preview-label">Así se verá tu estado de cuenta</div>
        <div class="doc">
          <div class="doc__bar">
            <img v-if="ob.park.logoPreview" :src="ob.park.logoPreview" alt="" class="doc__logo" />
            <span v-else class="doc__name">{{ ob.park.name.trim() || 'Nombre de tu parque' }}</span>
            <span class="doc__info">{{ infoLine }}</span>
          </div>
          <div class="doc__meta">Estado de cuenta · Folio EC-{{ year }}-{{ month }}-001 · Generado el {{ today }}</div>
          <div class="doc__skeleton">
            <i style="width: 62%"></i><i style="width: 84%"></i><i style="width: 48%"></i>
            <div class="doc__total"></div>
          </div>
          <div v-if="bankLine" class="doc__bank">
            <b>Datos para transferencia</b>
            <span>{{ bankLine }}</span>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ob } from '@/services/onboardingStore'
import { isValidRfc, isValidEmail, formatClabe, clabeDigits, isValidClabe } from './onboardingFormat'

const fileInput = ref(null)
const dragOver = ref(false)
const logoError = ref('')

// El RFC siempre en mayúsculas.
const rfc = computed({
  get: () => ob.park.rfc,
  set: (v) => { ob.park.rfc = String(v).toUpperCase().replace(/[^A-ZÑ&0-9]/g, '') },
})
const rfcOk = computed(() => isValidRfc(ob.park.rfc))
const emailOk = computed(() => isValidEmail(ob.park.contactEmail))
const clabeShown = computed(() => formatClabe(ob.park.bankClabe))
const clabeCount = computed(() => clabeDigits(ob.park.bankClabe).length)
const clabeOk = computed(() => isValidClabe(ob.park.bankClabe))

function onClabe(e) {
  ob.park.bankClabe = clabeDigits(e.target.value)
  e.target.value = formatClabe(ob.park.bankClabe)
}

function setLogo(file) {
  logoError.value = ''
  if (!file) return
  if (!/^image\/(png|jpeg|svg\+xml)$/.test(file.type)) return (logoError.value = 'Usa una imagen PNG, JPG o SVG.')
  if (file.size > 2 * 1024 * 1024) return (logoError.value = 'El logo no debe pesar más de 2 MB.')
  if (ob.park.logoPreview && ob.park.logoPreview.startsWith('blob:')) URL.revokeObjectURL(ob.park.logoPreview)
  ob.park.logoFile = file
  ob.park.logoPreview = URL.createObjectURL(file)
}
const onFile = (e) => setLogo(e.target.files?.[0])
const onDrop = (e) => { dragOver.value = false; setLogo(e.dataTransfer?.files?.[0]) }

// ---- Vista previa ----
const now = new Date()
const year = now.getFullYear()
const month = String(now.getMonth() + 1).padStart(2, '0')
const today = now.toLocaleDateString('es-MX')
const infoLine = computed(() => [ob.park.rfc.trim(), ob.park.contactEmail.trim()].filter(Boolean).join('  ·  '))
const bankLine = computed(() => {
  const parts = [ob.park.bankName.trim(), ob.park.bankBeneficiary.trim(), formatClabe(ob.park.bankClabe)]
  return parts.filter(Boolean).join(' · ')
})
</script>

<style scoped>
.park { display: grid; grid-template-columns: minmax(0, 560px) minmax(0, 300px); gap: 36px; justify-content: center; align-items: start; }
.park__preview { position: sticky; top: 8px; }
.preview-label { font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px; }

.drop { height: 80px; border: 2px dashed #cbd5e1; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; cursor: pointer; background: #fafbfd; transition: border-color 0.15s, background 0.15s; overflow: hidden; }
.drop:hover, .drop--over { border-color: #3b82f6; background: #eff6ff; }
.drop--has { border-style: solid; border-color: #e2e8f0; background: #fff; }
.drop__icon { font-size: 22px; color: #94a3b8; }
.drop__text { font: 400 12px Inter, sans-serif; color: #64748b; }
.drop__img { max-height: 64px; max-width: 80%; object-fit: contain; }

.doc { border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; overflow: hidden; box-shadow: 0 6px 20px rgba(30, 58, 95, 0.08); }
.doc__bar { background: #1e3a5f; min-height: 56px; padding: 10px 14px; display: flex; flex-direction: column; justify-content: center; gap: 4px; }
.doc__name { font: 700 14px Helvetica, Arial, sans-serif; color: #fff; word-break: break-word; }
.doc__logo { max-height: 30px; max-width: 140px; object-fit: contain; align-self: flex-start; }
.doc__info { font: 400 8px Helvetica, Arial, sans-serif; color: rgba(255, 255, 255, 0.8); min-height: 10px; word-break: break-all; }
.doc__meta { padding: 8px 14px; font: 400 8px Helvetica, Arial, sans-serif; color: #64748b; }
.doc__skeleton { padding: 4px 14px 14px; display: flex; flex-direction: column; gap: 7px; }
.doc__skeleton i { display: block; height: 6px; border-radius: 3px; background: #eef2f7; }
.doc__total { margin-top: 6px; height: 22px; border-radius: 4px; background: #f8f9fc; border: 1px solid #e2e8f0; }
.doc__bank { margin: 0 14px 14px; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 4px; display: flex; flex-direction: column; gap: 3px; font: 400 8px Helvetica, Arial, sans-serif; color: #1e3a5f; word-break: break-word; }
.doc__bank b { font-size: 9px; }

@media (max-width: 960px) {
  .park { grid-template-columns: minmax(0, 560px); }
  .park__preview { position: static; order: -1; }
}
</style>